import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';

import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getInvestorsAction } from 'store/accounting/actions';
import { selectInvestorsOptions } from 'store/accounting/selectors';
import { getMasterPermissionsToPage } from 'store/auth/selectors';

import { loanPLDetailsConfig } from '../LoanPL/LoanPL.config';
import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import { ContentHeadline } from '../sharedComponents';

import { getSellSideLockConfig } from './SellSideLock.config';
import { MANUAL_SELL_PRICE_HINT } from './SellSideLock.constants';

const SellSideLock = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
}) => {
  const dispatch = useDispatch();

  const investorsOptions = useSelector(selectInvestorsOptions);
  const { isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_PROFIT_AND_LOSS),
  );

  const investorPriceHelperText = applicationData.sellSideLock?.isSellPriceManualOverride
    ? MANUAL_SELL_PRICE_HINT
    : undefined;

  const config = useMemo(
    () =>
      getSellSideLockConfig({
        investorsOptions,
        disabled: !isManageAllowed,
        investorPriceHelperText,
      }),
    [investorsOptions, isManageAllowed, investorPriceHelperText],
  );

  useEffectSafeStrictMode(() => {
    dispatch(getInvestorsAction());
  }, [dispatch]);

  // Investor Price is the P&L block's Net Sell Price, and that block saves as a whole (a missing value
  // clears it), so every P&L field has to be registered in the form before this section can save it.
  useEffect(() => {
    setEntityConfig(loanPLDetailsConfig);
    setEntityConfig(config);
  }, [setEntityConfig, config]);

  return (
    <Box className={className} sx={boxStyle}>
      <ContentHeadline title="Sell Side Lock" />
      <FormGenerator
        isNestedForm
        changedFields={changedFields}
        config={config}
        disabledPendingChangesHighlight={disabledPendingChangesHighlight}
        form={form}
        formName={FormNames.SELL_SIDE_LOCK}
        handleChange={handleChange}
        isLoading={isSaving || isLoading}
        numberOfColumns={4}
        pendingChanges={pendingChanges}
        setInForm={setInForm}
        staticValidateField={staticValidateField}
        validateField={validateField}
        validationErrors={validationErrors}
      />
    </Box>
  );
};

SellSideLock.propTypes = pagesPropTypes;

export default memo(SellSideLock);
